// Small DOM helpers shared by the pages.
import { esc } from "./format.js";
import { toast } from "./toast.js";

export const $ = (s, r = document) => r.querySelector(s);
export const $$ = (s, r = document) => [...r.querySelectorAll(s)];

const SAFE_IMG = /^(https?:\/\/|\/uploads\/|data:image\/(png|jpe?g|webp|gif);)/i;
export const isSafeImage = (u) => typeof u === "string" && SAFE_IMG.test(u.trim());

export function initials(symbol, name) {
  const s = String(symbol || name || "?").replace(/^\$/, "").replace(/[^A-Za-z0-9]/g, "");
  return (s.slice(0, 2) || "?").toUpperCase();
}

/** Coin logo with initials underneath; a broken image falls back to the initials (see initImageFallback). */
export function coinLogo(logo, symbol, size = 44, name = "") {
  const img = isSafeImage(logo) ? `<img alt="" loading="lazy" decoding="async" src="${esc(logo.trim())}" data-fallback>` : "";
  return `<span class="coin-logo" style="--s:${size}px" aria-hidden="true">${esc(initials(symbol, name))}${img}</span>`;
}

/** X avatar: the profile picture when the server has one, over the handle's first letter (shown if it fails). */
export function avatar(h, src = "") {
  const c = String(h || "?").replace(/^@/, "").charAt(0) || "?";
  const img = isSafeImage(src) ? `<img alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer" src="${esc(src.trim())}" data-fallback>` : "";
  return `<span class="avatar" aria-hidden="true">${esc(c)}${img}</span>`;
}

export function initImageFallback() {
  document.addEventListener("error", (e) => {
    const t = e.target;
    if (t && t.tagName === "IMG" && t.hasAttribute("data-fallback")) t.remove();
  }, true);
}

export async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext !== false) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:-1000px;opacity:0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand("copy");
    ta.remove();
    return !!ok;
  } catch {
    return false;
  }
}

/** Wires a Copy button: shows "Copied" briefly, or tells the user to copy by hand. */
export function bindCopy(btn, getText, label = "Copy") {
  if (!btn) return;
  btn.addEventListener("click", async () => {
    const text = typeof getText === "function" ? getText() : getText;
    if (!text) return;
    if (await copyText(text)) {
      btn.textContent = "Copied";
      setTimeout(() => { btn.textContent = label; }, 1400);
    } else {
      toast(`Copy this by hand: ${text}`, 6000);
    }
  });
}

/** Busy state for a button: spinner, disabled, optional label while busy. */
export function setBusy(btn, busy, label) {
  if (!btn) return;
  if (busy) {
    if (!btn.dataset.label) btn.dataset.label = btn.textContent;
    if (label) btn.textContent = label;
    btn.classList.add("busy");
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
  } else {
    if (btn.dataset.label) btn.textContent = btn.dataset.label;
    delete btn.dataset.label;
    btn.classList.remove("busy");
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
  }
}

export function navigate(url) {
  window.location.assign(url);
}

export const intentUrl = (text) => `https://x.com/intent/post?text=${encodeURIComponent(text)}`;

/** Tweet/X status URL check, shared with the server's rule (x.com or twitter.com /<user>/status/<id>). */
export const isTweetUrl = (u) => /^https?:\/\/(www\.|mobile\.)?(x|twitter)\.com\/[A-Za-z0-9_]{1,15}\/status(es)?\/\d{5,25}(\/.*)?(\?.*)?$/i.test(String(u || "").trim());

export function note(kind, html) {
  const k = kind ? ` ${kind}` : "";
  return `<div class="note${k}" role="${kind === "err" ? "alert" : "status"}">${html}</div>`;
}

export const ICONS = {
  check: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7"></path></svg>',
  arrow: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4"></path></svg>',
  external: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 3h4v4M13 3L7 9M11.5 9.5V13H3V4.5h3.5"></path></svg>',
  chevron: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6l4 4 4-4"></path></svg>',
  search: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><circle cx="7" cy="7" r="4.5"></circle><path d="M10.5 10.5L14 14"></path></svg>',
  image: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="4"></rect><circle cx="9" cy="9" r="2"></circle><path d="M21 15l-5-5L5 21"></path></svg>',
};
